import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import moment from 'moment-timezone';
import { useNavigate } from 'react-router-dom';

const Sessions = () => {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
    const [filter, setFilter] = useState('all');
    const [retentionFilter, setRetentionFilter] = useState('all');
    const [customDate, setCustomDate] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'lastname', direction: 'asc' });
    const [statsDateFilter, setStatsDateFilter] = useState('hoy');
    const [statsCustomDate, setStatsCustomDate] = useState('');
    const [statsCustomRange, setStatsCustomRange] = useState({ start: '', end: '' });

    const [showSessionsModal, setShowSessionsModal] = useState(false);
    const [selectedUserSessions, setSelectedUserSessions] = useState([]);
    const [selectedUserName, setSelectedUserName] = useState('');
    const [lastUpdated, setLastUpdated] = useState(new Date());

    const ARGENTINA_TIMEZONE = 'America/Argentina/Buenos_Aires';

    // Memoizar funciones que no cambian
    const handleOnline = useCallback(() => {
        Swal.fire({
            icon: 'success',
            title: 'Conexión restablecida',
            text: 'Ahora estás online.',
        });
    }, []);

    const handleOffline = useCallback(() => {
        Swal.fire({
            icon: 'error',
            title: 'Sin conexión',
            text: 'Parece que estás offline. Verifica tu conexión a Internet.',
        });
    }, []);

    // Optimizar el debounce - solo actualizar cuando searchTerm cambie
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 400);

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    // Memoizar las funciones de utilidad que no cambian
    const getArgentinaDateString = useCallback((dateString) => {
        if (!dateString) return null;

        return moment(dateString).tz(ARGENTINA_TIMEZONE).format('YYYY-MM-DD');
    }, []);

    const getTodayArgentina = useCallback(() => {
        return moment().tz(ARGENTINA_TIMEZONE).format('YYYY-MM-DD');
    }, []);

    const enrichSessions = useCallback((sessionsData, phonesByUser) => {
        const currentWeekStart = moment().tz(ARGENTINA_TIMEZONE).startOf('week');
        const lastWeekStart = currentWeekStart.clone().subtract(1, 'week');
        const twoWeeksAgoStart = currentWeekStart.clone().subtract(2, 'weeks');
        const threeWeeksAgoStart = currentWeekStart.clone().subtract(3, 'weeks');
        const todayDay = moment().tz(ARGENTINA_TIMEZONE).day();

        const sessionsByUser = {};

        const enrichedSessions = sessionsData.map(session => {
            const loginTimestamp = moment(session.login).valueOf();
            const loginDateString = getArgentinaDateString(session.login);
            const userKey = session.userId ?? `${session.username}_${session.userlastname}`;
            const loginMoment = moment(session.login).tz(ARGENTINA_TIMEZONE);

            if (!sessionsByUser[userKey]) {
                sessionsByUser[userKey] = {
                    currentWeekSessions: 0,
                    previousWeekSessions: 0,
                    twoWeeksAgoSessions: 0,
                    threeWeeksAgoSessions: 0,
                    lastAttendanceTimestamp: 0
                };
            }

            if (loginMoment.isSameOrAfter(currentWeekStart)) {
                sessionsByUser[userKey].currentWeekSessions++;
            } else if (loginMoment.isSameOrAfter(lastWeekStart)) {
                sessionsByUser[userKey].previousWeekSessions++;
            } else if (loginMoment.isSameOrAfter(twoWeeksAgoStart)) {
                sessionsByUser[userKey].twoWeeksAgoSessions++;
            } else if (loginMoment.isSameOrAfter(threeWeeksAgoStart)) {
                sessionsByUser[userKey].threeWeeksAgoSessions++;
            }

            sessionsByUser[userKey].lastAttendanceTimestamp = Math.max(
                sessionsByUser[userKey].lastAttendanceTimestamp,
                loginTimestamp
            );

            return {
                ...session,
                userKey,
                loginTimestamp,
                loginDateString
            };
        });

        return enrichedSessions.map(session => {
            const userSessions = sessionsByUser[session.userKey];
            const sessionsWeek = Number(session.sessionsWeek) || 0;
            const hasCriticalDecline =
                userSessions.previousWeekSessions < userSessions.twoWeeksAgoSessions &&
                userSessions.twoWeeksAgoSessions < userSessions.threeWeeksAgoSessions;
            const hasInconsistencyRisk =
                (todayDay === 4 || todayDay === 5) &&
                sessionsWeek >= 5 &&
                userSessions.currentWeekSessions <= 1;
            const daysSinceLastAttendance = moment().tz(ARGENTINA_TIMEZONE)
                .startOf('day')
                .diff(moment(userSessions.lastAttendanceTimestamp).tz(ARGENTINA_TIMEZONE).startOf('day'), 'days');

            return {
                ...session,
                ...userSessions,
                phone: session.phone || phonesByUser[String(session.userKey)] || null,
                weeklyDifference: userSessions.currentWeekSessions - userSessions.previousWeekSessions,
                retentionStatus: hasCriticalDecline ? 'critical' : hasInconsistencyRisk ? 'inconsistent' : null,
                isAbsentOverTenDays: daysSinceLastAttendance > 10
            };
        });
    }, [getArgentinaDateString]);

    const fetchSessions = useCallback(async () => {
        try {
            const [response, membersResponse] = await Promise.all([
                axios.get('https://iron.nextriking.com/api/sessions/stats'),
                axios.get('https://iron.nextriking.com/api/users').catch(() => ({ data: { body: [] } }))
            ]);
            if (response.data.error) {
                throw new Error('Error en la respuesta del servidor');
            }
            const phonesByUser = (membersResponse.data.body || []).reduce((phones, member) => {
                if (!member.phone) return phones;
                phones[String(member.id)] = member.phone;
                phones[`${member.name}_${member.lastname}`] = member.phone;
                return phones;
            }, {});
            const processedSessions = enrichSessions(response.data.body, phonesByUser);
            setSessions(processedSessions);
            setError(null);
            setLastUpdated(new Date());
        } catch (error) {
            setError('Error al obtener las sesiones. Inténtalo de nuevo más tarde.');
            console.error('Error al obtener las sesiones:', error);
        }
    }, [enrichSessions]);

    useEffect(() => {
        fetchSessions();
        const interval = setInterval(fetchSessions, 60000);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            clearInterval(interval);
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [fetchSessions, handleOnline, handleOffline]);
    /* useEffect(() => {
        fetchSessions();
        const interval = setInterval(() => {
            fetchSessions();
        }, 60000);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            clearInterval(interval);
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [handleOnline, handleOffline]); */

    // Memoizar los datos filtrados para evitar recálculos innecesarios
    const filteredData = useMemo(() => {
        let filtered = sessions;

        const todayArgentina = getTodayArgentina();

        const weekStartArgentina = moment().tz(ARGENTINA_TIMEZONE).startOf('week').format('YYYY-MM-DD');

        if (filter === 'hoy') {
            filtered = sessions.filter(session => {
                return session.loginDateString === todayArgentina;
            });
        } else if (filter === 'semana') {
            filtered = sessions.filter(session => {
                return session.loginDateString >= weekStartArgentina;
            });
        } else if (filter === 'fecha' && customDate) {
            filtered = sessions.filter(session => {
                return session.loginDateString === customDate;
            });
        }

        if (debouncedSearchTerm && debouncedSearchTerm.length >= 3) {
            filtered = filtered.filter(session =>
                session.username.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
                session.userlastname.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
                session.dni.includes(debouncedSearchTerm)
            );
        }

        if (retentionFilter !== 'all') {
            const latestSessionByUser = new Map();
            filtered.forEach(session => {
                if (!latestSessionByUser.has(session.userKey) || latestSessionByUser.get(session.userKey).loginTimestamp < session.loginTimestamp) {
                    latestSessionByUser.set(session.userKey, session);
                }
            });
            filtered = Array.from(latestSessionByUser.values()).filter(session => {
                if (retentionFilter === 'active') return session.currentWeekSessions > 0 && session.weeklyDifference >= 0;
                if (retentionFilter === 'declining') return session.weeklyDifference < 0;
                return session.isAbsentOverTenDays;
            });
        }

        return filtered;
    }, [sessions, filter, customDate, debouncedSearchTerm, retentionFilter, getTodayArgentina, ARGENTINA_TIMEZONE]);

    // Memoizar los datos ordenados separadamente
    const sortedAndFilteredSessions = useMemo(() => {
        if (!sortConfig.key) return filteredData;

        return [...filteredData].sort((a, b) => {
            let valueA, valueB;

            switch (sortConfig.key) {
                case 'fullName':
                    valueA = `${a.username || ''} ${a.userlastname || ''}`.trim().toLowerCase();
                    valueB = `${b.username || ''} ${b.userlastname || ''}`.trim().toLowerCase();
                    break;
                case 'login':
                    valueA = a.loginTimestamp || 0;
                    valueB = b.loginTimestamp || 0;
                    break;
                case 'loginTime':
                    valueA = a.loginTimestamp || 0;
                    valueB = b.loginTimestamp || 0;
                    break;
                case 'service':
                    valueA = a.service || 0;
                    valueB = b.service || 0;
                    break;
                case 'todayRoutine':
                    valueA = (a.todayRoutine || '').toLowerCase();
                    valueB = (b.todayRoutine || '').toLowerCase();
                    break;
                case 'sessionId':
                    valueA = a.sessionId || 0;
                    valueB = b.sessionId || 0;
                    break;
                default:
                    valueA = a[sortConfig.key] ?? '';
                    valueB = b[sortConfig.key] ?? '';
            }

            if (typeof valueA === "number" && typeof valueB === "number") {
                return sortConfig.direction === "asc" ? valueA - valueB : valueB - valueA;
            }

            return sortConfig.direction === "asc"
                ? String(valueA).localeCompare(String(valueB), undefined, { sensitivity: "base" })
                : String(valueB).localeCompare(String(valueA), undefined, { sensitivity: "base" });
        });
    }, [filteredData, sortConfig]);

    const displayedSessions = useMemo(() => {
        return [...sortedAndFilteredSessions].reverse();
    }, [sortedAndFilteredSessions]);

    const retentionAlerts = useMemo(() => {
        const alertedUsers = new Map();

        sessions.forEach(session => {
            if (session.retentionStatus && !alertedUsers.has(session.userKey)) {
                alertedUsers.set(session.userKey, session);
            }
        });

        return Array.from(alertedUsers.values());
    }, [sessions]);

    const retentionKpis = useMemo(() => {
        const users = new Map();
        sessions.forEach(session => users.set(session.userKey, session));
        const uniqueUsers = Array.from(users.values());

        return {
            active: uniqueUsers.filter(session => session.currentWeekSessions > 0 && session.weeklyDifference >= 0).length,
            declining: uniqueUsers.filter(session => session.weeklyDifference < 0).length,
            absent: uniqueUsers.filter(session => session.isAbsentOverTenDays).length
        };
    }, [sessions]);

    /* const fetchSessions = async () => {
        try {
            const response = await axios.get('https://iron.nextriking.com/api/sessions/stats');
            if (response.data.error) {
                throw new Error('Error en la respuesta del servidor');
            }

            const processedSessions = enrichSessions(response.data.body);
            setSessions(processedSessions);
        } catch (error) {
            setError('Error al obtener las sesiones. Inténtalo de nuevo más tarde.');
            console.error('Error al obtener las sesiones:', error);
        }
    }; */

    const handleSort = useCallback((key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });
    }, [sortConfig]);

    const showUserSessions = useCallback((username, userlastname) => {
        const userKey = sessions.find(session => session.username === username && session.userlastname === userlastname)?.userKey
            ?? `${username}_${userlastname}`;

        const thirtyDaysAgoTimestamp = moment().tz(ARGENTINA_TIMEZONE).subtract(30, 'days').valueOf();

        const userSessions = sessions.filter(session => {
            const matchesUser = session.userKey === userKey;
            const isWithin30Days = session.loginTimestamp >= thirtyDaysAgoTimestamp;

            return matchesUser && isWithin30Days;
        });

        const sortedUserSessions = [...userSessions].sort((a, b) => b.loginTimestamp - a.loginTimestamp);

        setSelectedUserSessions(sortedUserSessions);
        setSelectedUserName(`${username} ${userlastname}`);
        setShowSessionsModal(true);
    }, [sessions, ARGENTINA_TIMEZONE]);

    const handleCloseModal = useCallback(() => {
        setShowSessionsModal(false);
        setSelectedUserSessions([]);
        setSelectedUserName('');
    }, []);

    const applyRetentionFilter = (nextFilter) => {
        setRetentionFilter(nextFilter);
        setFilter('all');
        setCustomDate('');
    };

    const openRetentionWhatsApp = useCallback((session) => {
        if (!session.phone) return;
        const message = `Hola ${session.username}, notamos que esta semana bajaste tus entrenamientos en comparación con la anterior. ¡Te extrañamos en la sala libre! ¿Todo bien?`;
        window.open(`https://wa.me/${session.phone.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
    }, []);
    /* <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginBottom: '20px',
                        justifyContent: 'space-between'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '24px' }}>👥</span>
                            <h3 style={{ margin: 0, color: '#333', fontSize: '20px' }}>Sesiones de Usuarios</h3>
                        </div>

                        <span style={{ fontSize: '12px', color: '#999', fontStyle: 'italic' }}>
                            🔄 Actualizado: {moment(lastUpdated).tz(ARGENTINA_TIMEZONE).format('HH:mm:ss')}
                        </span>
                    </div> */
    // Memoizar las estadísticas filtradas
    const filteredStatsUsers = useMemo(() => {
        return sessions.filter(user => {
            if (!user.login) return false;

            if (statsDateFilter === 'hoy') {
                const todayArgentina = getTodayArgentina();
                return user.loginDateString === todayArgentina;
            }

            if (statsDateFilter === 'semana') {
                const weekStartArgentina = moment().tz(ARGENTINA_TIMEZONE).startOf('week').format('YYYY-MM-DD');

                return user.loginDateString >= weekStartArgentina;
            }

            if (statsDateFilter === 'mes') {
                return moment(user.login).tz(ARGENTINA_TIMEZONE).isSame(moment().tz(ARGENTINA_TIMEZONE), 'month');
            }

            if (statsDateFilter === 'fecha') {
                if (!statsCustomDate) return false;
                return user.loginDateString === statsCustomDate;
            }

            if (statsDateFilter === 'rango') {
                if (!statsCustomRange.start || !statsCustomRange.end) return false;
                return user.loginDateString >= statsCustomRange.start && user.loginDateString <= statsCustomRange.end;
            }

            return true;
        });
    }, [sessions, statsDateFilter, statsCustomDate, statsCustomRange, getTodayArgentina, ARGENTINA_TIMEZONE]);

    const handleNavigation = (route) => {
        if (route === '/dashboard' || route === 'back') {
            if (window.history.length > 1) {
                navigate(-1);
                return;
            }

            navigate('/dashboard');
        } else {
            navigate(route);
        }
    };

    // Memoizar las estadísticas calculadas
    const stats = useMemo(() => {
        return filteredStatsUsers.reduce((acc, session) => {
            if (session.service === 1) acc.usersInSalaLibre++;
            if (session.service === 2) acc.usersInSalaHIFT++;
            if (session.service === 3) acc.usersInPersonal++;
            return acc;
        }, { usersInSalaLibre: 0, usersInSalaHIFT: 0, usersInPersonal: 0 });
    }, [filteredStatsUsers]);

    // Memoizar las columnas
    const columns = useMemo(() => [
        {
            text: 'Nombre',
            sortKey: 'fullName',
            render: (session) => (
                <span>
                    {session.username} {session.userlastname}
                    {session.phone && (session.weeklyDifference < 0 || session.retentionStatus) && (
                        <button
                            type="button"
                            onClick={() => openRetentionWhatsApp(session)}
                            title="Contactar por WhatsApp"
                            style={{
                                backgroundColor: '#25d366', border: 'none', borderRadius: '4px', color: 'white',
                                cursor: 'pointer', fontSize: '14px', marginLeft: '8px', padding: '3px 6px'
                            }}
                        >
                            <i className="bi bi-whatsapp" aria-hidden="true"></i>
                        </button>
                    )}
                </span>
            )
        },
        {
            text: 'Fecha',
            sortKey: 'login',
            render: (session) => new Date(session.login).toLocaleDateString('es-AR', {
                timeZone: 'America/Argentina/Buenos_Aires'
            })
        },
        {
            text: 'Horario de Login',
            sortKey: 'loginTime',
            render: (session) => moment(session.login)
                .tz(ARGENTINA_TIMEZONE)
                .format('hh:mm A')
        },
        {
            text: 'Servicio',
            sortKey: 'service',
            render: (session) => (
                <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    backgroundColor:
                        session.service === 1 ? '#d4edda' :
                            session.service === 2 ? '#fff3cd' : '#f8d7da',
                    color:
                        session.service === 1 ? '#155724' :
                            session.service === 2 ? '#856404' : '#721c24'
                }}>
                    {session.service === 1 ? "Sala libre" :
                        session.service === 2 ? "Sala HIFT" :
                            session.service === 3 ? "Personalizado" : "Desconocido"}
                </span>
            )
        },
        {
            text: 'Contratadas - semana',
            sortKey: 'sessionsWeek',
            render: (session) => session.sessionsWeek
        },
        {
            text: 'Sesión # en la semana',
            sortKey: 'sessionNumber',
            render: (session) => {
                const sessionExceeded = session.sessionNumber > session.sessionsWeek;
                return (
                    <span style={{
                        color: sessionExceeded ? '#dc3545' : 'inherit',
                        fontWeight: sessionExceeded ? 'bold' : 'normal'
                    }}>
                        {session.sessionNumber}
                    </span>
                );
            }
        },
        {
            text: (
                <span>
                    VS semana pasada
                    <span
                        style={{ marginLeft: '5px', cursor: 'help' }}
                        title={`🟢 Verde (+): Más asistencias que la semana pasada
🔴 Rojo (-): Menos asistencias que la semana pasada
⚫ Negro (0): Mismas asistencias que la semana pasada
Ejemplos:
Usuario A: Vino 2 esta semana y 4 la anterior → -2 (rojo)
Usuario B: Vino 5 esta semana y 3 la anterior → +2 (verde)
Usuario C: Vino 3 ambas semanas → 0 (negro)`}
                    >
                        ℹ️
                    </span>
                </span>
            ),
            sortKey: 'weeklyDifference',
            render: (session) => {
                const difference = session.weeklyDifference;
                const displayValue = difference === 0 ? '0' :
                    difference > 0 ? `+${difference}` : `-${Math.abs(difference)}`;

                if (difference !== 0) {
                    return (
                        <span
                            style={{
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                color: difference < 0 ? '#dc3545' : '#28a745',
                                fontWeight: 'bold'
                            }}
                            onClick={() => showUserSessions(session.username, session.userlastname)}
                            title="Click para ver sesiones de los últimos 30 días"
                        >
                            {displayValue}
                        </span>
                    );
                }

                return displayValue;
            }
        },
        {
            text: 'Semana #',
            sortKey: 'trainingWeek',
            render: (session) => (
                <span style={{
                    color: session.trainingWeek >= 14 ? "#28a745" : "#333",
                    fontWeight: session.trainingWeek >= 14 ? "bold" : "normal"
                }}>
                    {session.trainingWeek}
                </span>
            )
        },
        {
            text: 'Entrenamiento',
            sortKey: 'todayRoutine',
            render: (session) => session.todayRoutine
        },
        {
            text: 'ID',
            sortKey: 'sessionId',
            render: (session) => session.sessionId
        }
    ], [showUserSessions, openRetentionWhatsApp]);

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: '#f8f9fa',
            padding: '20px'
        }}>
            {/* Header */}
            <div style={{
                backgroundColor: '#4472c4',
                color: 'white',
                padding: '20px',
                borderRadius: '8px 8px 0 0',
                marginBottom: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        width: '50px',
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px'
                    }}>
                        📊
                    </div>
                    <h2 style={{ margin: 0, fontSize: '24px' }}>
                        Estadísticas de Horarios
                    </h2>
                </div>
                <button
                    onClick={() => handleNavigation('back')}
                    style={{
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        cursor: 'pointer'
                    }}
                >
                    ← Volver atrás
                </button>
            </div>

            {/* Contenedor principal */}
            <div style={{
                backgroundColor: 'white',
                padding: '30px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                maxWidth: '1400px',
                margin: '0 auto'
            }}>
                <div style={{
                    backgroundColor: '#f8f9fa',
                    border: '2px solid #e9ecef',
                    borderRadius: '12px',
                    padding: '25px',
                    marginBottom: '30px'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginBottom: '20px'
                    }}>
                        <span style={{ fontSize: '24px' }}>📊</span>
                        <h3 style={{ margin: 0, color: '#333', fontSize: '20px' }}>Estadísticas</h3>
                    </div>

                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '10px',
                        marginBottom: '20px',
                        justifyContent: 'center'
                    }}>
                        {['hoy', 'semana', 'mes', 'fecha', 'rango'].map(filterType => (
                            <button
                                key={filterType}
                                onClick={() => setStatsDateFilter(filterType)}
                                style={{
                                    backgroundColor: statsDateFilter === filterType ? '#4472c4' : 'white',
                                    color: statsDateFilter === filterType ? 'white' : '#4472c4',
                                    border: '2px solid #4472c4',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    textTransform: 'capitalize'
                                }}
                            >
                                {filterType}
                            </button>
                        ))}

                        {statsDateFilter === 'fecha' && (
                            <input
                                type="date"
                                value={statsCustomDate}
                                onChange={(e) => setStatsCustomDate(e.target.value)}
                                style={{
                                    padding: '8px 12px',
                                    border: '2px solid #ddd',
                                    borderRadius: '6px',
                                    fontSize: '14px'
                                }}
                            />
                        )}

                        {statsDateFilter === 'rango' && (
                            <>
                                <input
                                    type="date"
                                    value={statsCustomRange.start}
                                    onChange={(e) => setStatsCustomRange({ ...statsCustomRange, start: e.target.value })}
                                    style={{
                                        padding: '8px 12px',
                                        border: '2px solid #ddd',
                                        borderRadius: '6px',
                                        fontSize: '14px'
                                    }}
                                />
                                <input
                                    type="date"
                                    value={statsCustomRange.end}
                                    onChange={(e) => setStatsCustomRange({ ...statsCustomRange, end: e.target.value })}
                                    style={{
                                        padding: '8px 12px',
                                        border: '2px solid #ddd',
                                        borderRadius: '6px',
                                        fontSize: '14px'
                                    }}
                                />
                            </>
                        )}
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                        gap: '20px'
                    }}>
                        {[
                            { label: 'Total', value: filteredStatsUsers.length, icon: '👥', color: '#6c757d' },
                            { label: 'En Sala Libre', value: stats.usersInSalaLibre, icon: '🏋️', color: '#28a745' },
                            { label: 'En Sala HIFT', value: stats.usersInSalaHIFT, icon: '💪', color: '#ffc107' },
                            { label: 'Personalizado', value: stats.usersInPersonal, icon: '⭐', color: '#dc3545' }
                        ].map((stat, index) => (
                            <div
                                key={index}
                                style={{
                                    backgroundColor: 'white',
                                    border: `3px solid ${stat.color}`,
                                    borderRadius: '12px',
                                    padding: '20px',
                                    textAlign: 'center',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                }}
                            >
                                <div style={{ fontSize: '24px', marginBottom: '10px' }}>{stat.icon}</div>
                                <h4 style={{
                                    margin: '0 0 10px 0',
                                    fontSize: '14px',
                                    color: '#666',
                                    fontWeight: 'normal'
                                }}>
                                    {stat.label}
                                </h4>
                                <p style={{
                                    margin: 0,
                                    fontSize: '24px',
                                    fontWeight: 'bold',
                                    color: stat.color
                                }}>
                                    {stat.value}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '24px',
                        marginBottom: '20px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '24px' }}>👥</span>
                            <h3 style={{ margin: 0, color: '#333', fontSize: '20px' }}>Sesiones de Usuarios</h3>
                        </div>

                        <span style={{ fontSize: '12px', color: '#000000', fontStyle: 'bold' }}>
                                🔄 Actualizado: {moment(lastUpdated).tz(ARGENTINA_TIMEZONE).format('HH:mm:ss')}
                        </span>
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
                        gap: '14px',
                        marginBottom: '20px'
                    }}>
                        {[
                            { key: 'active', title: 'Activos esta semana', value: retentionKpis.active, detail: 'Ver estables o en alza', color: '#198754' },
                            { key: 'declining', title: 'Riesgo de deserción', value: retentionKpis.declining, detail: 'Ver variación negativa', color: '#d97706' },
                            { key: 'absent', title: 'Alerta roja', value: retentionKpis.absent, detail: 'Ausentes más de 10 días', color: '#dc3545' }
                        ].map(kpi => (
                            <button
                                key={kpi.key}
                                type="button"
                                onClick={() => applyRetentionFilter(kpi.key)}
                                style={{
                                    backgroundColor: retentionFilter === kpi.key ? `${kpi.color}15` : 'white',
                                    border: `2px solid ${kpi.color}`,
                                    borderRadius: '10px',
                                    color: '#212529',
                                    cursor: 'pointer',
                                    padding: '16px',
                                    textAlign: 'left'
                                }}
                            >
                                <div style={{ color: kpi.color, fontSize: '13px', fontWeight: 'bold', textTransform: 'uppercase' }}>{kpi.title}</div>
                                <div style={{ color: kpi.color, fontSize: '30px', fontWeight: 'bold', lineHeight: '1.2', margin: '4px 0' }}>{kpi.value}</div>
                                <div style={{ fontSize: '13px' }}>{kpi.detail}</div>
                            </button>
                        ))}
                    </div>

                    {retentionFilter !== 'all' && (
                        <button
                            type="button"
                            onClick={() => {
                                setRetentionFilter('all');
                                setFilter('all');
                            }}
                            style={{
                                background: 'none', border: 'none', color: '#4472c4', cursor: 'pointer',
                                fontSize: '13px', marginBottom: '12px', padding: 0, textDecoration: 'underline'
                            }}
                        >
                            Quitar filtro de retención
                        </button>
                    )}

                    {retentionAlerts.length > 0 && (
                        <div style={{
                            backgroundColor: '#fff3cd',
                            border: '1px solid #ffc107',
                            borderRadius: '8px',
                            color: '#664d03',
                            marginBottom: '20px',
                            padding: '14px 16px'
                        }}>
                            <strong>Alertas de retención ({retentionAlerts.length})</strong>
                            <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {retentionAlerts.map(session => (
                                    <span key={session.userKey} style={{
                                        backgroundColor: session.retentionStatus === 'critical' ? '#f8d7da' : '#fff',
                                        border: `1px solid ${session.retentionStatus === 'critical' ? '#dc3545' : '#ffc107'}`,
                                        borderRadius: '999px',
                                        color: session.retentionStatus === 'critical' ? '#842029' : '#664d03',
                                        padding: '4px 8px'
                                    }}>
                                        {session.username} {session.userlastname}: {session.retentionStatus === 'critical' ? 'Crítico' : 'Inconstancia'}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {error && (
                        <div style={{
                            backgroundColor: '#f8d7da',
                            color: '#721c24',
                            padding: '15px',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            border: '1px solid #f5c6cb'
                        }}>
                            {error}
                        </div>
                    )}

                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '15px',
                        marginBottom: '25px',
                        alignItems: 'center'
                    }}>
                        <input
                            type="text"
                            placeholder="Buscar por nombre o DNI"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                flex: '1',
                                minWidth: '250px',
                                padding: '12px 16px',
                                border: '2px solid #ddd',
                                borderRadius: '8px',
                                fontSize: '14px'
                            }}
                        />

                        <button
                            onClick={() => {
                                setFilter('hoy');
                                setRetentionFilter('all');
                            }}
                            style={{
                                backgroundColor: filter === 'hoy' ? '#4472c4' : 'white',
                                color: filter === 'hoy' ? 'white' : '#4472c4',
                                border: '2px solid #4472c4',
                                padding: '12px 20px',
                                borderRadius: '8px',
                                fontSize: '14px',
                                cursor: 'pointer'
                            }}
                        >
                            Hoy
                        </button>

                        <button
                            onClick={() => {
                                setFilter('semana');
                                setRetentionFilter('all');
                            }}
                            style={{
                                backgroundColor: filter === 'semana' ? '#4472c4' : 'white',
                                color: filter === 'semana' ? 'white' : '#4472c4',
                                border: '2px solid #4472c4',
                                padding: '12px 20px',
                                borderRadius: '8px',
                                fontSize: '14px',
                                cursor: 'pointer'
                            }}
                        >
                            Semana
                        </button>

                        <input
                            type="date"
                            value={customDate}
                            onChange={(e) => {
                                setCustomDate(e.target.value);
                                setFilter('fecha');
                                setRetentionFilter('all');
                            }}
                            style={{
                                padding: '12px 16px',
                                border: '2px solid #ddd',
                                borderRadius: '8px',
                                fontSize: '14px'
                            }}
                        />
                    </div>

                    <div style={{
                        backgroundColor: 'white',
                        border: '2px solid #e9ecef',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: '14px'
                            }}>
                                <thead>
                                    <tr style={{
                                        backgroundColor: '#4472c4',
                                        color: 'white'
                                    }}>
                                        {columns.map((column, index) => (
                                            <th key={index} style={{
                                                padding: '15px 12px',
                                                textAlign: 'left',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                borderRight: '1px solid rgba(255,255,255,0.2)',
                                                backgroundColor: sortConfig.key === column.sortKey ? 'rgba(255,255,255,0.1)' : 'transparent'
                                            }}
                                                onClick={() => handleSort(column.sortKey)}
                                            >
                                                {column.text}
                                                {sortConfig.key === column.sortKey && (
                                                    <span style={{ marginLeft: '5px' }}>
                                                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayedSessions.map((session, index) => {
                                        const isEvenRow = index % 2 === 0;
                                        const rowBackgroundColor = session.retentionStatus === 'critical'
                                            ? '#f8d7da'
                                            : session.retentionStatus === 'inconsistent'
                                                ? '#fff3cd'
                                                : isEvenRow ? '#f8f9fa' : 'white';

                                        return (
                                            <tr key={session.sessionId} style={{
                                                backgroundColor: rowBackgroundColor
                                            }}>
                                                {columns.map((column, colIndex) => (
                                                    <td key={colIndex} style={{
                                                        height: '52px',
                                                        padding: '12px',
                                                        textAlign: colIndex >= 4 && colIndex <= 7 ? 'center' : 'left',
                                                        fontWeight: colIndex === 0 ? '500' : 'normal',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {typeof column.render === 'function' ? column.render(session) : session[column.sortKey]}
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {showSessionsModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '30px',
                        maxWidth: '600px',
                        width: '90%',
                        maxHeight: '80vh',
                        overflow: 'auto',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '20px'
                        }}>
                            <h3 style={{ margin: 0, color: '#333' }}>
                                Sesiones de {selectedUserName}
                            </h3>
                            <button
                                onClick={handleCloseModal}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    color: '#666'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={{ overflowX: 'auto' }}>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: '14px'
                            }}>
                                <thead>
                                    <tr style={{
                                        backgroundColor: '#f8f9fa',
                                        borderBottom: '2px solid #dee2e6'
                                    }}>
                                        <th style={{ padding: '12px', textAlign: 'left' }}>Fecha</th>
                                        <th style={{ padding: '12px', textAlign: 'left' }}>Horario</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedUserSessions.map((session, index) => (
                                        <tr key={`${session.sessionId}-${index}`} style={{
                                            borderBottom: '1px solid #dee2e6'
                                        }}>
                                            <td style={{ padding: '12px' }}>
                                                {new Date(session.login).toLocaleDateString('es-AR', {
                                                    timeZone: 'America/Argentina/Buenos_Aires',
                                                    weekday: 'long',
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                            </td>
                                            <td style={{ padding: '12px' }}>
                                                {new Date(session.login).toLocaleTimeString('es-AR', {
                                                    timeZone: 'America/Argentina/Buenos_Aires',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    hour12: true
                                                })}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {selectedUserSessions.length === 0 && (
                                <p style={{
                                    textAlign: 'center',
                                    color: '#666',
                                    fontStyle: 'italic',
                                    padding: '20px'
                                }}>
                                    No se encontraron sesiones para este usuario.
                                </p>
                            )}
                        </div>

                        <div style={{
                            textAlign: 'right',
                            marginTop: '20px',
                            paddingTop: '20px',
                            borderTop: '1px solid #dee2e6'
                        }}>
                            <button
                                onClick={handleCloseModal}
                                style={{
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    cursor: 'pointer'
                                }}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Sessions;
